import { useQueryClient } from '@tanstack/react-query';
import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { IAIAgentTemplate, IAIAgentTemplateAttachment } from '../../../../extViewmodels/sdk/models/AI';
import { MastheadPortalId } from '../../../../models/AdminModels';
import { useAdminUserSession } from '../../../../models/hooks/adminStateHooks';
import { useErrorMessages } from '../../../../models/hooks/appStateHooks';
import { useAIAgentTemplateQuery } from '../../../../queries/AIAgentTemplate';
import { useCloneAIAgentTemplateMutation } from '../../../../queries/AIAgentTemplate/useCloneAIAgentTemplateMutation';
import { useCreateAIAgentTemplateMutation } from '../../../../queries/AIAgentTemplate/useCreateAIAgentTemplateMutation';
import { useUpdateAIAgentTemplateMutation } from '../../../../queries/AIAgentTemplate/useUpdateAIAgentTemplateMutation';
import { AdminPermission } from '../../../../viewmodels/AdminViewModels';
import { AIAssistantDialog } from '../../../../web/components-v2/ai/AIAssistantDialog';
import { TextArea } from '../../../../web/components-v2/TextArea';
import { Button } from '../../../../web/components/Button';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { NavigationBreadcrumbsBar } from '../../../../web/components/NavigationBreadcrumbsBar';
import { Portal } from '../../../../web/components/Portal';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../web/components/Select';
import { TextInput } from '../../../../web/components/TextInput';
import { bs } from '../../../../web/styles/styles';
import { AIAgentTemplateAttachments } from '../components/AIAgentTemplateAttachments';
import { AIAgentTemplateVariables } from '../components/AIAgentTemplateVariables';
import {
	availableModels,
	conversationTypePermissions,
	getFilteredConversationTypes,
	verticalOptions,
} from '../constants';
import { getAssistantNameFromTemplateName, isSalesRelatedConversationType } from '../utils';
import { AIAgentTemplateFormData } from '../validation';
import { styles } from './styles';

const EMPTY_FORM_DATA: AIAgentTemplateFormData = {
	name: '',
	description: '',
	objectivePrompt: '',
	technicalPrompt: '',
	model: '',
	conversationType: '',
	selectors: {},
	variables: {},
	attachments: [],
};

function getInitialFormData(template: IAIAgentTemplate | undefined, isCloneMode: boolean): AIAgentTemplateFormData {
	if (!template) {
		return EMPTY_FORM_DATA;
	}
	return {
		name: isCloneMode ? `${template.name} (Copy)` : template.name,
		description: template.description || '',
		objectivePrompt: template.objectivePrompt || '',
		technicalPrompt: template.technicalPrompt || '',
		model: template.model || '',
		conversationType: template.conversationType || '',
		selectors: template.selectors || {},
		variables: template.variables || {},
		attachments: template.attachments || [],
	};
}

interface AIAgentTemplateFormProps {
	initialData: AIAgentTemplateFormData;
	aiAgentTemplate: IAIAgentTemplate | undefined;
	isNewMode: boolean;
	isCloneMode: boolean;
	templateId: string;
}

function AIAgentTemplateForm({
	initialData,
	aiAgentTemplate,
	isNewMode,
	isCloneMode,
	templateId,
}: AIAgentTemplateFormProps) {
	const history = useHistory();
	const queryClient = useQueryClient();
	const userSession = useAdminUserSession();
	const errorMessages = useErrorMessages();

	// State for test chat modal
	const [isTestChatOpen, setIsTestChatOpen] = React.useState(false);

	// Get filtered conversation types based on user permissions
	const availableConversationTypes = React.useMemo(() => {
		return getFilteredConversationTypes(userSession.permissions || {});
	}, [userSession.permissions]);

	const [formData, setFormData] = React.useState<AIAgentTemplateFormData>(initialData);
	const [isSubmitting, setIsSubmitting] = React.useState(false);
	const [formErrors, setFormErrors] = React.useState<Record<string, string>>({});

	// Check if user has permission to modify system prompts
	const canModifySystemPrompts = React.useMemo(() => {
		return userSession.hasPermission(AdminPermission.ModifySystemPrompts);
	}, [userSession]);

	// Mutation hooks for API calls
	const updateMutation = useUpdateAIAgentTemplateMutation({
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['aiAgentTemplatesQuery'] });
			queryClient.invalidateQueries({ queryKey: ['aiAgentTemplateQuery', templateId] });
			toast.success('AI Agent updated successfully');
			history.push('/ai-agent-templates');
		},
		onError: error => {
			errorMessages.pushApiError(error);
		},
	});

	const createMutation = useCreateAIAgentTemplateMutation({
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['aiAgentTemplatesQuery'] });
			toast.success('AI Agent created successfully');
			history.push('/ai-agent-templates');
		},
		onError: error => {
			errorMessages.pushApiError(error);
		},
	});

	const cloneMutation = useCloneAIAgentTemplateMutation({
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['aiAgentTemplatesQuery'] });
			toast.success('AI Agent cloned successfully');
			history.push('/ai-agent-templates');
		},
		onError: error => {
			errorMessages.pushApiError(error);
		},
	});

	// Simple validation function
	const validateForm = () => {
		const errors: Record<string, string> = {};

		if (!formData.name.trim()) {
			errors.name = 'Name is required';
		} else if (formData.name.length > 255) {
			errors.name = 'Name must be less than 255 characters';
		}

		if (formData.description && formData.description.length > 1000) {
			errors.description = 'Description must be less than 1000 characters';
		}

		if (!formData.objectivePrompt.trim()) {
			errors.objectivePrompt = 'Objective prompt is required';
		} else if (formData.objectivePrompt.length > 110000) {
			errors.objectivePrompt = 'Objective prompt must be less than 10000 characters';
		}

		if (formData.technicalPrompt && formData.technicalPrompt.length > 10000) {
			errors.technicalPrompt = 'Technical prompt must be less than 10000 characters';
		}

		if (!formData.model.trim()) {
			errors.model = 'Model is required';
		}

		if (!formData.conversationType.trim()) {
			errors.conversationType = 'Agent type is required';
		}

		return errors;
	};

	// Form submission handler
	const onSubmit = async () => {
		setIsSubmitting(true);
		setFormErrors({});

		// Validate form
		const errors = validateForm();
		if (Object.keys(errors).length > 0) {
			setFormErrors(errors);
			setIsSubmitting(false);
			return;
		}

		try {
			if (isNewMode) {
				await createMutation.mutateAsync({ data: formData });
			} else if (isCloneMode) {
				await cloneMutation.mutateAsync({
					originalId: templateId,
					data: formData,
				});
			} else {
				// For updates, spread the original template data to preserve fields not in the form
				const updateData: AIAgentTemplateFormData = {
					...aiAgentTemplate,
					...formData,
				};
				await updateMutation.mutateAsync({
					id: templateId,
					data: updateData,
				});
			}
		} catch (error) {
			errorMessages.pushApiError(error);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleFormSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		onSubmit();
	};

	const handleBack = () => {
		history.push('/ai-agent-templates');
	};

	// Get current form values for conditional rendering
	const conversationType = formData.conversationType;
	const currentSelectors = formData.selectors;

	const handleAttachmentsChange = (attachments: IAIAgentTemplateAttachment[]) => {
		setFormData(prev => ({ ...prev, attachments }));
	};

	const pageTitle = isNewMode ? 'Create AI Agent' : isCloneMode ? 'Clone AI Agent' : 'Edit AI Agent';

	const pathComponentNameProvider = (pathComponent: string) => {
		// For the AI Agent ID path component, show the template name instead of the ID
		if (pathComponent === templateId && aiAgentTemplate && aiAgentTemplate.name) {
			return aiAgentTemplate.name;
		}
		// For "new", show a more user-friendly name
		if (pathComponent === 'new') {
			return 'New AI Agent';
		}
		// For "ai-agent-templates", show a more user-friendly name
		if (pathComponent === 'ai-agent-templates') {
			return 'AI Agents';
		}
		// Return undefined to use the default behavior
		return undefined;
	};

	return (
		<div className={css(styles.container)}>
			<Portal destination={MastheadPortalId}>
				<NavigationBreadcrumbsBar pathComponentNameProvider={pathComponentNameProvider} />
			</Portal>
			<div className={css(styles.header)}>
				<h1 className={css(styles.title)}>{pageTitle}</h1>
				<div className={css(styles.actions)}>
					<Button kind='secondary' onClick={handleBack}>
						Back
					</Button>
					{!isNewMode && !isCloneMode && (
						<Button kind='secondary' onClick={() => setIsTestChatOpen(true)}>
							Test
						</Button>
					)}
					<Button
						kind='primary'
						type='submit'
						form='ai-agent-template-form'
						disabled={isSubmitting || updateMutation.isPending || createMutation.isPending || cloneMutation.isPending}
					>
						{isNewMode ? 'Create' : isCloneMode ? 'Clone' : 'Save'}
					</Button>
				</div>
			</div>

			<form id='ai-agent-template-form' onSubmit={handleFormSubmit} className={css(styles.form)}>
				<div className={css(styles.formGroup)}>
					<label className={css(styles.label)}>Agent Type</label>
					<Select
						items={availableConversationTypes as { label: string; value: string }[]}
						value={formData.conversationType || ''}
						onValueChange={value => setFormData(prev => ({ ...prev, conversationType: value }))}
						disabled={isSubmitting}
					>
						<SelectTrigger styles={[bs.wFull]}>
							<SelectValue placeholder='Select agent type' />
						</SelectTrigger>
						<SelectContent>
							{availableConversationTypes.map(type => (
								<SelectItem key={type.value} value={type.value}>
									{type.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
					{formErrors.conversationType && <div className={css(styles.errorMessage)}>{formErrors.conversationType}</div>}
				</div>

				<div className={css(styles.formGroup)}>
					<label className={css(styles.label)}>Name</label>
					<TextInput
						type='text'
						value={formData.name}
						onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
						placeholder='Enter prompt name'
						disabled={isSubmitting}
					/>
					{formErrors.name && <div className={css(styles.errorMessage)}>{formErrors.name}</div>}
				</div>

				<div className={css(styles.formGroup)}>
					<label className={css(styles.label)}>Description</label>
					<TextArea
						styles={[styles.textArea]}
						value={formData.description}
						onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
						placeholder='Enter template description'
						disabled={isSubmitting}
					/>
					{formErrors.description && <div className={css(styles.errorMessage)}>{formErrors.description}</div>}
				</div>

				<div className={css(styles.formGroup)}>
					<label className={css(styles.label)}>Model</label>
					<Select
						items={availableModels}
						value={formData.model || ''}
						onValueChange={value => setFormData(prev => ({ ...prev, model: value }))}
						disabled={isSubmitting}
					>
						<SelectTrigger styles={[bs.wFull]}>
							<SelectValue placeholder='Select AI model' />
						</SelectTrigger>
						<SelectContent>
							{availableModels.map(model => (
								<SelectItem key={model.value} value={model.value}>
									{model.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
					{formErrors.model && <div className={css(styles.errorMessage)}>{formErrors.model}</div>}
				</div>

				<div className={css(styles.formGroup)}>
					<label className={css(styles.label)}>Objective Prompt</label>
					<TextArea
						styles={[styles.objectivePromptArea]}
						value={formData.objectivePrompt}
						onChange={e => setFormData(prev => ({ ...prev, objectivePrompt: e.target.value }))}
						placeholder='Enter objective prompt'
						disabled={isSubmitting}
					/>
					{formErrors.objectivePrompt && <div className={css(styles.errorMessage)}>{formErrors.objectivePrompt}</div>}
				</div>

				<div className={css(styles.formGroup)}>
					<label className={css(styles.label)}>Technical Prompt</label>
					<TextArea
						styles={[styles.technicalPromptArea]}
						value={formData.technicalPrompt}
						onChange={
							canModifySystemPrompts
								? e => setFormData(prev => ({ ...prev, technicalPrompt: e.target.value }))
								: undefined
						}
						placeholder='Enter technical prompt'
						readOnly={!canModifySystemPrompts}
						disabled={isSubmitting}
					/>
					{!canModifySystemPrompts && (
						<div className={css(styles.infoMessage)}>
							You need the ModifySystemPrompts permission to edit this field.
						</div>
					)}
					{formErrors.technicalPrompt && <div className={css(styles.errorMessage)}>{formErrors.technicalPrompt}</div>}
				</div>

				{isSalesRelatedConversationType(conversationType) && (
					<div className={css(styles.formGroup)}>
						<label className={css(styles.label)}>Vertical (Optional)</label>
						<div className={css(styles.selectorsContainer)}>
							<div className={css(styles.selectorRow)}>
								<Select
									value={currentSelectors?.Vertical || ''}
									onValueChange={value =>
										setFormData(prev => ({
											...prev,
											selectors: {
												...prev.selectors,
												Vertical: value,
											},
										}))
									}
									disabled={isSubmitting}
									items={verticalOptions}
								>
									<SelectTrigger styles={[bs.wFull]}>
										<SelectValue placeholder='Select vertical (optional)' />
									</SelectTrigger>
									<SelectContent>
										{verticalOptions.map(option => (
											<SelectItem key={option.value} value={option.value}>
												{option.label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
								{currentSelectors?.Vertical && (
									<Button
										kind='secondary'
										onClick={() =>
											setFormData(prev => ({ ...prev, selectors: { ...prev.selectors, Vertical: undefined } }))
										}
										disabled={isSubmitting}
									>
										Clear
									</Button>
								)}
							</div>
						</div>
					</div>
				)}

				<AIAgentTemplateVariables
					variables={formData.variables}
					onChange={variables => setFormData(prev => ({ ...prev, variables }))}
					objectivePrompt={formData.objectivePrompt}
					disabled={isSubmitting}
				/>

				<AIAgentTemplateAttachments
					attachments={formData.attachments}
					onChange={handleAttachmentsChange}
					templateId={!isNewMode && !isCloneMode ? templateId : undefined}
				/>
			</form>

			{/* Test Chat Modal */}
			{aiAgentTemplate && (
				<AIAssistantDialog
					open={isTestChatOpen}
					onOpenChange={setIsTestChatOpen}
					assistantName={getAssistantNameFromTemplateName(aiAgentTemplate.name)}
					conversationType={
						(aiAgentTemplate.conversationType as Api.AIConversationType) || Api.AIConversationType.SiteWide
					}
					agentTemplateId={aiAgentTemplate.id}
					title={`Test: ${aiAgentTemplate.name}`}
					initialMessage={`Hello! I'm the ${aiAgentTemplate.name} agent. How can I help you today?`}
					placeholder='Send a test message...'
				/>
			)}
		</div>
	);
}

export function AIAgentTemplateDetail() {
	const location = useLocation();
	const { id } = useParams<{ id: string }>();
	const userSession = useAdminUserSession();

	const isNewMode = location.pathname === '/ai-agent-templates/new' || id === 'new';
	const isCloneMode = new URLSearchParams(location.search).get('mode') === 'clone';

	const {
		data: aiAgentTemplate,
		isPending,
		isFetching,
		error: queryError,
	} = useAIAgentTemplateQuery({ id: isNewMode ? '' : id, enabled: !isNewMode && !!id });

	// Check if user has permission to view/edit this template
	const hasPermissionForTemplate = React.useMemo(() => {
		if (isNewMode || !aiAgentTemplate?.conversationType) return true;

		if (aiAgentTemplate.conversationType in conversationTypePermissions) {
			const requiredPermission =
				conversationTypePermissions[aiAgentTemplate.conversationType as keyof typeof conversationTypePermissions];
			return userSession.permissions?.[requiredPermission] === true;
		}

		return true;
	}, [aiAgentTemplate?.conversationType, userSession.permissions, isNewMode]);

	// For new mode, render the form immediately
	if (isNewMode) {
		return (
			<AIAgentTemplateForm
				key='new'
				initialData={EMPTY_FORM_DATA}
				aiAgentTemplate={undefined}
				isNewMode={true}
				isCloneMode={false}
				templateId={id}
			/>
		);
	}

	// Show loading state until data is ready
	// Use isFetching to also wait during refetch (e.g., when navigating between templates)
	// Check that the loaded template matches the requested id to avoid stale data issues
	const isDataReady = aiAgentTemplate && aiAgentTemplate.id === id;
	if (isPending || isFetching || !isDataReady) {
		return (
			<div className={css(styles.container)}>
				<div className={css(styles.header)}>
					<h1 className={css(styles.title)}>Loading...</h1>
				</div>
				<LoadingSpinner />
			</div>
		);
	}

	if (queryError) {
		return (
			<div className={css(styles.container)}>
				<div className={css(styles.header)}>
					<h1 className={css(styles.title)}>Error</h1>
				</div>
				<p>Error loading AI Agent. Please try again.</p>
			</div>
		);
	}

	if (!hasPermissionForTemplate) {
		return (
			<div className={css(styles.container)}>
				<div className={css(styles.header)}>
					<h1 className={css(styles.title)}>Access Denied</h1>
				</div>
				<p>You do not have permission to view or edit this AI Agent.</p>
			</div>
		);
	}

	// Render form only after data is available
	// Using key={id} forces React to remount the form when navigating to a different template,
	// ensuring the form state is re-initialized with the correct values
	return (
		<AIAgentTemplateForm
			key={id}
			initialData={getInitialFormData(aiAgentTemplate, isCloneMode)}
			aiAgentTemplate={aiAgentTemplate}
			isNewMode={false}
			isCloneMode={isCloneMode}
			templateId={id}
		/>
	);
}
